import { useEffect, useState } from 'react'
import { watchClubCourses, saveClubCourse, deleteClubCourse } from '../lib/data.js'
import { normalizeCourse, parseCourseCsv } from '../lib/course.js'
import { searchCourses } from '../lib/golfCourseApi.js'
import { PbccHeroHeader } from '../components/PbccLayout.jsx'

const EMPTY = () => ({
  name: '', teeName: 'Blue', color: '', rating: '', slope: '',
  holes: Array.from({ length: 18 }, (_, i) => ({ par: 4, si: i + 1 })),
})

// Course library — add/edit/delete the courses the club plays: per tee a name +
// color + rating + slope + 18-hole par + stroke index, by hand or CSV import.
// Scoring/handicap read rating/slope/stroke-index from the chosen tee.
export default function CourseLibrary() {
  const [courses, setCourses] = useState({})
  const [draft, setDraft] = useState(EMPTY())
  const [csv, setCsv] = useState('')
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchErr, setSearchErr] = useState('')
  useEffect(() => watchClubCourses(setCourses), [])

  // Course lookup via the app's existing no-key back door (ggCourseSearch /
  // bundled courses_db). It supplies course name + rating/slope per tee; per-hole
  // par + stroke index aren't in this source, so those load as editable defaults.
  const runSearch = async () => {
    const q = query.trim()
    if (q.length < 3) return
    setSearching(true); setSearchErr(''); setResults([])
    try { setResults(await searchCourses(q)) }
    catch { setSearchErr('Lookup unavailable.') }
    finally { setSearching(false) }
  }
  const loadFromLookup = (c) => {
    const tee = (c.teeBoxes || [])[0] || {}
    const hasHoles = Array.isArray(c.pars) && c.pars.length === 18
    setDraft({
      name: c.name || '',
      teeName: tee.name || 'Blue',
      color: '',
      rating: tee.rating || '',
      slope: tee.slope || '',
      holes: Array.from({ length: 18 }, (_, i) => ({
        par: hasHoles ? (c.pars[i] ?? 4) : 4,
        si: Array.isArray(c.strokeIndex) && c.strokeIndex.length === 18 ? (c.strokeIndex[i] ?? i + 1) : i + 1,
      })),
    })
    setResults([])
    setQuery('')
  }

  const setHole = (i, key, v) => setDraft((d) => ({ ...d, holes: d.holes.map((h, k) => (k === i ? { ...h, [key]: v } : h)) }))

  const teeFromDraft = () => ({
    name: draft.teeName || 'Default', color: draft.color || '',
    rating: Number(draft.rating) || 0, slope: Number(draft.slope) || 0,
    pars: draft.holes.map((h) => Number(h.par) || 4),
    strokeIndex: draft.holes.map((h) => Number(h.si) || 1),
    par: draft.holes.reduce((a, h) => a + (Number(h.par) || 0), 0),
  })

  const save = async () => {
    if (!draft.name.trim()) return
    setBusy(true)
    try { await saveClubCourse({ name: draft.name.trim(), tees: [teeFromDraft()] }); setDraft(EMPTY()) }
    finally { setBusy(false) }
  }

  const importCsv = () => {
    if (!csv.trim()) return
    const parsed = parseCourseCsv(csv, { name: draft.name || 'Imported Course' })
    const tee = parsed.tees[0]
    setDraft({
      name: parsed.name, teeName: tee.name, color: tee.color, rating: tee.rating || '', slope: tee.slope || '',
      holes: Array.from({ length: 18 }, (_, i) => ({ par: tee.pars[i] ?? 4, si: tee.strokeIndex[i] ?? i + 1 })),
    })
    setCsv('')
  }

  const list = Object.entries(courses).map(([id, c]) => ({ id, ...normalizeCourse(c) }))

  return (
    <div>
      <PbccHeroHeader variant="admin" />
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="card">
          <h2 className="text-lg font-bold mb-3">Courses ({list.length})</h2>
          {list.length === 0 ? <p className="text-sm text-gray-500">No courses yet.</p> : (
            <ul className="divide-y divide-gray-100">
              {list.map((c) => (
                <li key={c.id} className="py-2 flex items-center justify-between">
                  <span className="text-sm">
                    <b>{c.name}</b> — {c.tees.map((t) => `${t.name} (${t.rating}/${t.slope})`).join(', ') || 'no tees'}
                  </span>
                  <button className="text-red-600 text-xs font-bold" onClick={() => { if (confirm(`Delete ${c.name}?`)) deleteClubCourse(c.id) }}>Delete</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card space-y-3">
          <h2 className="text-lg font-bold">Add course</h2>

          <div className="flex flex-wrap items-end gap-2">
            <label className="flex-1 min-w-[220px]">
              <span className="text-xs uppercase tracking-wider text-gray-500">Look up a course</span>
              <input
                className="input mt-1"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch() } }}
                placeholder="Course name (3+ letters)"
              />
            </label>
            <button className="btn-secondary text-sm" onClick={runSearch} disabled={searching || query.trim().length < 3}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
          {searchErr ? <p className="text-sm font-bold" style={{ color: '#B91C1C' }}>{searchErr}</p> : null}
          {results.length > 0 ? (
            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
              {results.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => loadFromLookup(c)} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                    <span className="font-semibold text-sm">{c.name}</span>
                    <span className="text-xs text-gray-500"> {c.location ? `· ${c.location}` : ''} {(c.teeBoxes || []).length ? `· ${c.teeBoxes.length} tee${c.teeBoxes.length === 1 ? '' : 's'}` : ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Labeled label="Course"><input className="input" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} /></Labeled>
            <Labeled label="Tee"><input className="input" value={draft.teeName} onChange={(e) => setDraft((d) => ({ ...d, teeName: e.target.value }))} /></Labeled>
            <Labeled label="Color"><input className="input" value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} /></Labeled>
            <Labeled label="Rating"><input className="input" type="number" step="0.1" value={draft.rating} onChange={(e) => setDraft((d) => ({ ...d, rating: e.target.value }))} /></Labeled>
            <Labeled label="Slope"><input className="input" type="number" value={draft.slope} onChange={(e) => setDraft((d) => ({ ...d, slope: e.target.value }))} /></Labeled>
          </div>

          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500">
                  <th className="text-left pr-2">Hole</th>
                  {draft.holes.map((_, i) => <th key={i} className="px-1 w-10 text-center">{i + 1}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="pr-2 font-bold text-gray-500">Par</td>
                  {draft.holes.map((h, i) => (
                    <td key={i} className="px-1"><input className="input py-1 px-1 text-center w-10" value={h.par} onChange={(e) => setHole(i, 'par', e.target.value)} /></td>
                  ))}
                </tr>
                <tr>
                  <td className="pr-2 font-bold text-gray-500">S.I.</td>
                  {draft.holes.map((h, i) => (
                    <td key={i} className="px-1"><input className="input py-1 px-1 text-center w-10" value={h.si} onChange={(e) => setHole(i, 'si', e.target.value)} /></td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <button className="btn-primary" onClick={save} disabled={busy || !draft.name.trim()}>{busy ? 'Saving…' : 'Save course'}</button>
        </div>

        <div className="card space-y-2">
          <h2 className="text-lg font-bold">CSV import</h2>
          <textarea className="input font-mono text-xs" rows={5} value={csv} onChange={(e) => setCsv(e.target.value)}
            placeholder={'name=Pebble\ntee=Blue\nrating=72.4\nslope=133\n1,4,5\n2,5,11\n…'} />
          <button className="btn-secondary text-sm" onClick={importCsv} disabled={!csv.trim()}>Load into form</button>
        </div>
      </div>
    </div>
  )
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
